import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Box, Typography, Paper, Alert, Button, LinearProgress, Chip,
  Divider, Switch, FormControlLabel, TextField, Skeleton, Dialog,
  DialogTitle, DialogContent, DialogActions, ToggleButton,
  ToggleButtonGroup, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Tooltip,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getBilling, createCheckoutSession, getCheckoutStatus, updateOverage } from '../../api/developerApi';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import { assertHttps, safeHttpsUrl } from '../../utils/assertHttps';

// ---------------------------------------------------------------------------
// Plan pricing — sourced entirely from the billing API's `availablePlans`
// catalog (backend SoT: products.json / SubscriptionPlanDB), NEVER hardcoded
// here. Historically this component carried literal prices/credits that drifted
// from what checkout actually charged (#325); the numbers now flow from the same
// data the Stripe price is resolved from, so they can't disagree.
// ---------------------------------------------------------------------------

// A hosted-invoice link comes from the backend (from Stripe). It is rendered only
// when it is an https URL, and the href is the value that was checked.
function HostedInvoiceButton({ url }) {
  const href = safeHttpsUrl(url);
  if (!href) return null;
  return (
    <Button
      size="small"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
      sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
    >
      View
    </Button>
  );
}

// Format integer cents as a display price for the given interval. Whole-dollar
// amounts render without cents ("$29/mo"); fractional amounts show two decimals.
function formatPrice(cents, interval) {
  if (cents == null) return '—';
  const dollars = cents / 100;
  const amount = Number.isInteger(dollars)
    ? `$${dollars.toLocaleString()}`
    : `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return interval === 'Yearly' ? `${amount}/yr` : `${amount}/mo`;
}

// The yearly discount, computed from the catalog rather than asserted — the old
// "Save 20%" chip was itself drifted (the real yearly saving is ~2 months free).
function yearlySavingsPct(plans) {
  let max = 0;
  for (const p of plans) {
    const m = p.prices?.Monthly;
    const y = p.prices?.Yearly;
    if (m && y) max = Math.max(max, Math.round((1 - y / (m * 12)) * 100));
  }
  return max;
}

// ---------------------------------------------------------------------------
// UpgradeDialog
// ---------------------------------------------------------------------------
function UpgradeDialog({ open, onClose, onConfirm, loading, plans }) {
  const [selected, setSelected] = useState(null);
  const [billingInterval, setBillingInterval] = useState('Monthly');

  // Reset selections each time dialog opens — default to the popular plan.
  useEffect(() => {
    if (open) {
      const def = plans.find((p) => p.popular) || plans[0];
      setSelected(def ? def.planId : null);
      setBillingInterval('Monthly');
    }
  }, [open, plans]);
  const plan = plans.find((p) => p.planId === selected);
  const savingsPct = yearlySavingsPct(plans);
  // Guard: never send a plan to checkout on an interval it has no price for —
  // the backend would reject it ("No price found"). Belt-and-suspenders; the
  // catalog normally carries both intervals for every plan.
  const selectedPriceMissing = plan != null && plan.prices?.[billingInterval] == null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upgrade plan</DialogTitle>
      <DialogContent dividers>
        <ToggleButtonGroup
          value={billingInterval}
          exclusive
          onChange={(_, v) => v && setBillingInterval(v)}
          size="small"
          sx={{ mb: 3 }}
        >
          <ToggleButton value="Monthly">Monthly</ToggleButton>
          <ToggleButton value="Yearly">
            Yearly
            {savingsPct > 0 && (
              <Chip label={`Save ${savingsPct}%`} size="small" sx={{ ml: 1, bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
            )}
          </ToggleButton>
        </ToggleButtonGroup>

        {plans.length === 0 && (
          <Alert severity="info">
            Plans are temporarily unavailable. Please try again shortly, or contact support if this persists.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          {plans.map((p) => (
            <Paper
              key={p.planId}
              onClick={() => setSelected(p.planId)}
              elevation={0}
              sx={{
                flex: 1, p: 2.5, cursor: 'pointer', borderRadius: 2,
                border: selected === p.planId ? '2px solid #3B82F6' : '1px solid #E4E4E7',
                bgcolor: selected === p.planId ? '#EFF6FF' : 'background.paper',
              }}
            >
              <Typography fontWeight={700} mb={0.5}>{p.name}</Typography>
              <Typography variant="h6" fontWeight={800} mb={0.5}>{formatPrice(p.prices?.[billingInterval], billingInterval)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {p.monthlyCredits.toLocaleString()} credits/mo
              </Typography>
              <Typography variant="body2" color="text.secondary">Overage billing available</Typography>
            </Paper>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || !plan || selectedPriceMissing}
          onClick={() => onConfirm(selected, billingInterval)}
          sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' } }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Upgrade to ${plan?.name ?? 'plan'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PLAN_CHANGE_MESSAGES = {
  applied: 'Plan updated.',
  scheduled: 'Downgrade scheduled for the end of the billing period.',
  pending: 'Your plan change is pending payment confirmation and has not been applied.',
};

// After a completed payment-method SETUP the card is saved by an asynchronous Stripe webhook, which can land
// after the browser's one status call and one refetch (#1539). The page then refetches `billing` for a short,
// bounded time: every SAVED_CARD_REFETCH_INTERVAL_MS, at most SAVED_CARD_REFETCH_MAX_ATTEMPTS times (about
// 20 s), stopping at the first response that carries a card or when the page is left.
const SAVED_CARD_REFETCH_INTERVAL_MS = 2000;
const SAVED_CARD_REFETCH_MAX_ATTEMPTS = 10;

// ---------------------------------------------------------------------------
// BillingPage
// ---------------------------------------------------------------------------
export default function BillingPage() {
  const queryClient = useQueryClient();
  const [sessionBanner, setSessionBanner] = useState(null); // 'success' | 'error' | null
  const [planChange, setPlanChange] = useState(null); // 'applied' | 'scheduled' | null
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [overageEnabled, setOverageEnabled] = useState(false);
  const [overageBudget, setOverageBudget] = useState('');
  const [overageError, setOverageError] = useState('');
  const [overageSaving, setOverageSaving] = useState(false);

  const { data: billing, isLoading } = useQuery('billing', getBilling, {
    onSuccess: (d) => {
      setOverageEnabled(d.overageEnabled ?? false);
      setOverageBudget(d.overageBudgetCents ? String(Math.round(d.overageBudgetCents / 100)) : '');
    },
  });

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return undefined;
    // Clean URL immediately
    window.history.replaceState({}, '', '/billing');

    let cancelled = false;
    let timer = null;
    const hasSavedCard = () => Boolean(queryClient.getQueryData('billing')?.paymentMethod);
    const pause = (ms) => new Promise((resolve) => { timer = setTimeout(resolve, ms); });

    // Never rejects and never touches the banner: a failed refetch, or the cap, leaves the page exactly as it is
    // (the success banner stays, no error is shown), and the next manual reload shows whatever has landed.
    const waitForSavedCard = async (firstRefetch) => {
      try { await firstRefetch; } catch { /* keep waiting */ }
      for (let attempt = 0; attempt < SAVED_CARD_REFETCH_MAX_ATTEMPTS; attempt += 1) {
        if (cancelled || hasSavedCard()) return;
        await pause(SAVED_CARD_REFETCH_INTERVAL_MS);
        if (cancelled) return;
        try { await queryClient.refetchQueries('billing', { exact: true }); } catch { /* keep waiting */ }
      }
    };

    getCheckoutStatus(sessionId)
      .then((s) => {
        if (s.status === 'complete') {
          setSessionBanner('success');
          const firstRefetch = queryClient.invalidateQueries('billing');
          // Only a payment-METHOD setup is saved by the async webhook; a subscription session is unchanged.
          if (s.mode === 'setup') waitForSavedCard(firstRefetch);
        } else {
          setSessionBanner('error');
        }
      })
      .catch(() => setSessionBanner('error'));

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCheckout = async (body) => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    setPlanChange(null);
    try {
      const { url, applied, scheduled } = await createCheckoutSession(body);
      // An account that already has a subscription is changed in place, so the
      // response reports the outcome and carries no checkout url: applied, scheduled,
      // or (both false) held by Stripe as a pending update. Anything else without a
      // url falls through to the https guard.
      const outcome = applied === true ? 'applied'
        : scheduled === true ? 'scheduled'
        : applied === false && scheduled === false ? 'pending'
        : null;
      if (url == null && outcome) {
        setPlanChange(outcome);
        setUpgradeOpen(false);
        setCheckoutLoading(false);
        queryClient.invalidateQueries('billing');
        return;
      }
      window.location.href = assertHttps(url, 'checkout link');
    } catch (e) {
      // Previously swallowed — the user saw the spinner stop and nothing else.
      setCheckoutError(e);
      setCheckoutLoading(false);
    }
  };

  const handleUpgradeConfirm = (planId, billingInterval) => {
    startCheckout({ mode: 'subscription', planId, billingInterval });
  };

  const handlePaymentMethod = () => {
    startCheckout({ mode: 'setup' });
  };

  const handleSaveOverage = async () => {
    setOverageError('');
    setOverageSaving(true);
    try {
      const budgetCents = overageEnabled ? Math.round(parseFloat(overageBudget) * 100) : null;
      if (overageEnabled && (!overageBudget || isNaN(budgetCents) || budgetCents <= 0)) {
        setOverageError('Enter a valid monthly budget.');
        setOverageSaving(false);
        return;
      }
      if (overageEnabled && budgetCents > 50000) {
        setOverageError('Monthly budget cap cannot exceed $500.');
        setOverageSaving(false);
        return;
      }
      await updateOverage({ enabled: overageEnabled, monthlyBudgetCents: budgetCents });
      queryClient.invalidateQueries('billing');
    } catch (e) {
      setOverageError(e?.response?.data?.message || 'Failed to save overage settings.');
    } finally {
      setOverageSaving(false);
    }
  };

  const plan = billing?.plan ?? 'free';
  const planDisplay = billing?.planDisplayName ?? 'Free';
  const creditsUsed = billing?.creditsUsed ?? 0;
  const planLimit = billing?.planLimitCredits ?? null;
  const pct = planLimit ? Math.min(100, Math.round((creditsUsed / planLimit) * 100)) : 0;
  const isPaid = plan !== 'free';
  // #388 paid-upgrade gate: when off (free-only launch), hide every entry point
  // into the Stripe checkout (upgrade CTA + add/update payment method). Default
  // false when the field is absent — fail closed, matching the server-side guard.
  const paidUpgradeEnabled = billing?.paidUpgradeEnabled ?? false;
  const hasPaymentMethod = !!billing?.paymentMethod;
  const invoices = billing?.invoices ?? [];
  // Plan catalog for the upgrade dialog — prices/credits from the backend SoT (#325).
  const availablePlans = billing?.availablePlans ?? [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Billing</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Manage your plan and payment details</Typography>

      {/* Return from Checkout banner */}
      {sessionBanner === 'success' && (
        <Alert severity="success" onClose={() => setSessionBanner(null)} sx={{ mb: 3 }}>
          Payment successful — your plan has been updated.
        </Alert>
      )}
      {sessionBanner === 'error' && (
        <Alert severity="error" onClose={() => setSessionBanner(null)} sx={{ mb: 3 }}>
          Checkout was not completed. No changes were made.
        </Alert>
      )}

      {planChange && (
        <Alert
          severity={planChange === 'pending' ? 'warning' : 'success'}
          onClose={() => setPlanChange(null)}
          sx={{ mb: 3 }}
        >
          {PLAN_CHANGE_MESSAGES[planChange]}
        </Alert>
      )}

      <ApiErrorAlert
        error={checkoutError}
        onClose={() => setCheckoutError(null)}
        title="Could not start checkout"
        sx={{ mb: 3 }}
      />

      {/* Branding disclosure — always visible, not dismissible */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Billing is managed by <strong>Halespring Public Benefit Corporation (d/b/a Vectros)</strong>,
        the company behind the Vectros API. You'll be redirected to our secure payment processor
        to complete any payment actions.
      </Alert>

      {/* Plan card */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>Current Plan</Typography>
            {isLoading
              ? <Skeleton width={80} />
              : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>{planDisplay}</Typography>
                  <Chip label={plan} size="small" sx={{ bgcolor: isPaid ? '#EFF6FF' : '#F4F4F5', color: isPaid ? '#1D4ED8' : '#52525B', fontWeight: 600 }} />
                </Box>}
          </Box>
          {!isLoading && !isPaid && paidUpgradeEnabled && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setUpgradeOpen(true)}
              disabled={planChange === 'pending'}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' } }}
            >
              Upgrade
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">Credits used this period</Typography>
          {isLoading
            ? <Skeleton width={80} />
            : <Typography variant="body2" fontWeight={600}>
                {creditsUsed.toLocaleString()} / {planLimit ? planLimit.toLocaleString() : '∞'}
              </Typography>}
        </Box>
        {isLoading
          ? <Skeleton height={8} sx={{ borderRadius: 1 }} />
          : planLimit
            ? <LinearProgress variant="determinate" value={pct} sx={{
                height: 8, borderRadius: 1, bgcolor: '#DBEAFE',
                '& .MuiLinearProgress-bar': {
                  bgcolor: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#3B82F6',
                },
              }} />
            : <Typography variant="caption" color="text.secondary">Unlimited credits</Typography>}
        {!isLoading && planLimit && (
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            Resets monthly
          </Typography>
        )}
      </Paper>

      {/* Payment method — hidden entirely on the free-only launch (#388) when
          there's no card to show AND no path to add one (paid checkout gated). */}
      {(isLoading || hasPaymentMethod || paidUpgradeEnabled) && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>Payment Method</Typography>
          <Divider sx={{ mb: 2 }} />
          {isLoading
            ? <Skeleton width={200} />
            : hasPaymentMethod
              ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2">
                      {billing.paymentMethod.brand.charAt(0).toUpperCase() + billing.paymentMethod.brand.slice(1)}{' '}
                      •••• {billing.paymentMethod.last4}
                    </Typography>
                  </Box>
                  {paidUpgradeEnabled && (
                    <Button size="small" variant="outlined" onClick={handlePaymentMethod} disabled={checkoutLoading}
                      sx={{ textTransform: 'none', borderColor: '#E4E4E7', color: 'text.primary' }}>
                      Update card
                    </Button>
                  )}
                </Box>
              : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">No payment method saved</Typography>
                  {paidUpgradeEnabled && (
                    <Button size="small" variant="outlined" onClick={handlePaymentMethod} disabled={checkoutLoading}
                      sx={{ textTransform: 'none', borderColor: '#E4E4E7', color: 'text.primary' }}>
                      {checkoutLoading ? <CircularProgress size={14} /> : 'Add payment method'}
                    </Button>
                  )}
                </Box>}
        </Paper>
      )}

      {/* Overage billing */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Overage Billing</Typography>
          {!isPaid && <Chip label="Paid plans only" size="small" sx={{ bgcolor: '#F4F4F5', color: '#52525B' }} />}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary" mb={2}>
          When enabled, usage beyond your plan limit is billed at $0.01 per credit.
          Set a monthly budget cap to limit your maximum overage exposure.
        </Typography>
        <Tooltip title={!isPaid ? 'Upgrade to a paid plan to enable overage billing' : ''} placement="top-start">
          <span>
            <FormControlLabel
              control={
                <Switch
                  checked={overageEnabled}
                  onChange={(e) => setOverageEnabled(e.target.checked)}
                  disabled={!isPaid || isLoading}
                />
              }
              label={<Typography variant="body2">Enable overage billing</Typography>}
              sx={{ mb: overageEnabled ? 2 : 0 }}
            />
          </span>
        </Tooltip>
        {overageEnabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <TextField
              label="Monthly budget cap"
              size="small"
              value={overageBudget}
              onChange={(e) => setOverageBudget(e.target.value)}
              InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography> }}
              helperText="Maximum overage charges per month (up to $500)"
              sx={{ width: 220 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveOverage}
              disabled={overageSaving}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, alignSelf: 'flex-start', mt: 0.25 }}
            >
              {overageSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
          </Box>
        )}
        {overageError && <Alert severity="error" sx={{ mt: 2 }}>{overageError}</Alert>}
      </Paper>

      {/* Invoice history */}
      {(isLoading || invoices.length > 0) && (
        <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2 }}>
          <Box sx={{ p: 3, pb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>Invoice History</Typography>
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell />
                  </TableRow>
                ))
                : invoices.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>
                      {inv.periodStart
                        ? new Date(inv.periodStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                      ${((inv.amountPaid ?? inv.amountDue) / 100).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inv.status}
                        size="small"
                        sx={{
                          bgcolor: inv.status === 'paid' ? '#f0fdf4' : '#fef2f2',
                          color: inv.status === 'paid' ? '#166534' : '#991b1b',
                          fontWeight: 600, fontSize: 11,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <HostedInvoiceButton url={inv.hostedInvoiceUrl} />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Upgrade dialog */}
      <UpgradeDialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        onConfirm={handleUpgradeConfirm}
        loading={checkoutLoading}
        plans={availablePlans}
      />
    </Box>
  );
}
